import { Maybe, Option } from "@jutro/types";
import { AddIcon, Button, Tooltip } from "@jutro/ui";
import dayjs, { Dayjs } from "dayjs";
import pl from "dayjs/locale/pl";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { SetContainerRef } from "components/AnimatedHeaderOnScrollWrapper/types";
import {
  EdmMedicalAppShiftWithoutVisitFragment,
  EdmNonWorkingPeriod,
} from "lib/graphql/megaSchema";
import { useVojevodshipOptions } from "lib/hooks/useVojevodshipOptions";
import { Block } from "views/StaffSchedule/StaffScheduleCreator/components/Block";
import { BlockDetailsTooltip } from "views/StaffSchedule/StaffScheduleCreator/components/BlockDetailsTooltip";
import { StaffDetails } from "views/StaffSchedule/StaffScheduleCreator/components/StaffDetails";
import {
  SlotActionModalMode,
  StaffOption,
  getColumnBackgroundColor,
  isMedicalAppShift,
} from "views/StaffSchedule/StaffScheduleCreator/tools";
import { EmptyDoctorsList } from "views/StaffSchedule/components/EmptyDoctorsList";

type Props = {
  filteredMedicalStaff: Maybe<Option<StaffOption>[]>;
  searchInputValue: string;
  days: Dayjs[];
  selectedOrganizationId: string;
  shiftsData: Maybe<
    (EdmMedicalAppShiftWithoutVisitFragment | EdmNonWorkingPeriod)[]
  >;
  setSearchInputValue: Dispatch<SetStateAction<string>>;
  setSelectedBlockStaffId: Dispatch<SetStateAction<string>>;
  setSelectedBlock: Dispatch<
    SetStateAction<
      EdmMedicalAppShiftWithoutVisitFragment | EdmNonWorkingPeriod | undefined
    >
  >;
  handleBlockActionModalOpen: (mode: SlotActionModalMode, day?: Dayjs) => void;
  handleStaffDetailsActionModalOpen: (
    staff: Option<StaffOption> | null,
  ) => void;
  setContainerRef: SetContainerRef;
};

export const Schedule = (props: Props) => {
  const {
    filteredMedicalStaff,
    searchInputValue,
    days,
    shiftsData,
    selectedOrganizationId,
    setSearchInputValue,
    setSelectedBlockStaffId,
    setSelectedBlock,
    handleBlockActionModalOpen,
    handleStaffDetailsActionModalOpen,
    setContainerRef,
  } = props;

  const ref = useRef<HTMLDivElement>(null);

  const { organizationsClinicsOptions } = useVojevodshipOptions();

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    setContainerRef(ref);
  }, [ref]);

  if (!filteredMedicalStaff || filteredMedicalStaff.length === 0) {
    return (
      <EmptyDoctorsList
        searchInputValue={searchInputValue}
        onSearchInputValueReset={() => setSearchInputValue("")}
        mode="scheduleCreator"
        onModalOpen={() => handleBlockActionModalOpen("create")}
      />
    );
  }

  const BLOCK_WIDTH = "w-16";
  const STAFF_WIDTH = "w-64";

  return (
    <div
      className="border-jutro-new-warm-gray-300 w-fit max-w-full overflow-x-auto rounded-2xl border"
      ref={ref}
    >
      <div className="relative flex w-fit flex-1 flex-col bg-white">
        <div className="border-jutro-new-warm-gray-300 [&>div]:border-jutro-new-warm-gray-300 sticky top-0 z-30 flex w-full border-b bg-white [&>div]:border-r">
          <div
            className={`font-paragraph-1 sticky left-0 z-1 flex rounded-tl-lg bg-white ${STAFF_WIDTH} items-center justify-center`}
          >
            Personel medyczny
          </div>
          {days.map((day) => (
            <div
              key={day.format("MM-DD")}
              className={`flex ${BLOCK_WIDTH} flex-1 flex-col text-center last-of-type:border-none ${getColumnBackgroundColor(
                day,
              )}`}
            >
              <span>{day.locale(pl).format("dd")}</span>
              <span>{day.format("D")}</span>
            </div>
          ))}
        </div>

        {filteredMedicalStaff.map((staff) => (
          <div
            key={staff.value.id}
            className="border-jutro-new-warm-gray-300 flex border-b bg-white last-of-type:border-none"
          >
            <div
              className={`h-inherit border-jutro-new-warm-gray-300 sticky left-0 z-20 flex border-r ${STAFF_WIDTH} group bg-white p-2`}
            >
              <StaffDetails
                staff={staff}
                handleStaffDetailsActionModalOpen={
                  handleStaffDetailsActionModalOpen
                }
              />
            </div>

            {days.map((day) => (
              <div
                key={`${staff.value.id}${day.format("MM-DD")}`}
                className={`group flex ${BLOCK_WIDTH} border-jutro-new-warm-gray-300 flex-1 cursor-pointer flex-col gap-1 border-r p-1 last-of-type:border-none ${getColumnBackgroundColor(
                  day,
                )}`}
              >
                {shiftsData?.map((shift) => {
                  const isDeletable = Boolean(
                    isMedicalAppShift(shift) && shift.state === "PRE_DELETED",
                  );

                  return (
                    dayjs(shift.day).get("D") === day.get("D") &&
                    shift.staff.id === staff.value.id && (
                      <Tooltip
                        position="top"
                        key={`${shift.id}`}
                        trigger={
                          <div>
                            <Block
                              shift={shift}
                              selectedOrganizationId={selectedOrganizationId}
                              isDeletable={isDeletable}
                              openModal={(mode) => {
                                handleBlockActionModalOpen(mode, day);
                                setSelectedBlockStaffId(staff.value.id);

                                if (mode === "edit") {
                                  setSelectedBlock(shift);
                                }
                              }}
                            />
                          </div>
                        }
                        content={
                          <BlockDetailsTooltip
                            shift={shift}
                            staff={staff}
                            shiftLocalization={
                              isMedicalAppShift(shift)
                                ? (organizationsClinicsOptions.find(
                                    (clinic) =>
                                      clinic.value === shift.organizationId,
                                  )?.label ?? "")
                                : "Brak danych"
                            }
                          />
                        }
                      />
                    )
                  );
                })}

                {!shiftsData?.some(
                  (shift) =>
                    dayjs(shift.day).get("D") === day.get("D") &&
                    shift.staff.id === staff.value.id,
                ) && (
                  <div
                    className="group-hover:bg-jutro-new-blue-50 m-auto h-full w-full items-center justify-center rounded-lg group-hover:flex"
                    onClick={() => {
                      handleBlockActionModalOpen("create", day);
                      setSelectedBlockStaffId(staff.value.id);
                    }}
                  >
                    <div className="hidden items-center justify-center group-hover:flex">
                      <Button
                        label="addSlot"
                        icon={<AddIcon size="sm" />}
                        variant="ghost"
                        size="condensed"
                        withTooltip={false}
                        onClick={() => {
                          handleBlockActionModalOpen("create", day);
                          setSelectedBlockStaffId(staff.value.id);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
